'use client';

import { ButtonBase, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import { Form } from '@unform/web';
import { GiPositionMarker } from 'react-icons/gi';

import { DrawerComponent } from '../Drawer';
import { SelectComponent } from '../Select';
import { ButtonComponent } from '../Button';
import LoadingComponent from '../Loading';

import styles from './styles.module.scss';

import data from '@/data/states_cities.json';
import { useLocationContext } from '@/context/LocalizationContext';
import { LocalizationRepository } from '@/repositories/localization';

export function LocalizationComponent() {
  const { localization, setLocalization } = useLocationContext();
  const [modal, setModal] = useState(false);

  const [errorMap, setErrorMap] = useState('');

  const [uf, setUf] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [cities, setCities] = useState([]);
  const [positionUser, setPositionUser] = useState({
    lat: null,
    lng: null
  });

  const [viewSelect, setViewSelect] = useState(false);
  const [loading, setLoading] = useState(true);

  const storage = typeof window !== 'undefined' && localStorage.getItem('@localization');
  const sendForm = () => {
    if (state && city) {
      setLocalization({
        state,
        uf: state,
        city
      });

      localStorage.setItem(
        '@localization',
        JSON.stringify({
          state,
          uf: state,
          city
        })
      );
      setModal(false);
      setErrorMap('');
    }

    if (!state || !city) {
      setErrorMap('Selecione o estado e a cidade');
    }
  };

  const states = data.states.map(item => ({
    value: item.state,
    label: item.state,
    uf: item.uf
  }));

  useEffect(() => {
    if (state) {
      setViewSelect(false);
      setUf(states.find(item => item.value === state)?.uf);

      const filter =
        state &&
        data.states
          .find(item => item.state === state)
          ?.cities.map(item => ({
            value: item,
            label: item
          }));

      setCities(filter);

      setTimeout(() => {
        setViewSelect(true);
      }, 1000);
    }
  }, [state]);

  useEffect(() => {
    if (localization.state && localization.city) {
      setState(localization.state);
      setCity(localization.city);
      setModal(false);
      setLoading(false);
      setErrorMap('');
    }

    if (errorMap) {
      setModal(true);
      setLoading(false);
    }
  }, [localization, errorMap]);

  useEffect(() => {
    if (!storage && positionUser.lat && positionUser.lng) {
      getLocalization(positionUser.lat, positionUser.lng);
      setErrorMap('');
    }
  }, [positionUser]);

  const getLocalization = async (latitude: number, longitude: number) => {
    const initLocation = await LocalizationRepository.getGeoLocation({ latitude, longitude });

    if (initLocation) {
      setLocalization({
        state: initLocation.state,
        uf: initLocation.state,
        city: initLocation.city
      });

      localStorage.setItem(
        '@localization',
        JSON.stringify({
          state: initLocation.state,
          uf: initLocation.state,
          city: initLocation.city
        })
      );

      setLoading(false);
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };

          setPositionUser(pos);
        },
        () => {
          setErrorMap('Não foi possível encontrar sua localização');
        }
      );
    } else {
      // Browser doesn't support Geolocation
      setErrorMap('Seu navegador não suporta geolocalização');
    }
  }, [typeof window !== 'undefined' && window.navigator.geolocation]);

  return (
    <>
      <div className={styles.localizationContainer}>
        <Tooltip title="Sua localização">
          <ButtonBase
            className={styles.button}
            onClick={() => {
              if (!state || !city) return null;

              return setModal(!modal);
            }}
          >
            <span className={styles.marker}>
              <GiPositionMarker color="white" />
            </span>
            {loading ? (
              <LoadingComponent mini color="neutral" />
            ) : (
              <div className={styles.cityContent}>
                <p className={styles.city}>
                  {localization.city} / {uf}
                </p>
              </div>
            )}
          </ButtonBase>
        </Tooltip>
      </div>
      <DrawerComponent
        drawer={modal}
        setDrawer={() => {
          if (localization.state && localization.city) return setModal(false);

          return null;
        }}
        height="calc(100% - var(--height-header))"
        className={styles.drawer}
      >
        {errorMap && (
          <div className={styles.error}>
            <p className={styles.errorTitle}>{errorMap}</p>
          </div>
        )}

        <p className={styles.title}>Onde você está?</p>
        <p className={styles.description}>
          Para que possamos encontrar os melhores resultados para você, precisamos saber onde você está.
        </p>
        {positionUser && (
          <>
            <p className={styles.subTitle}>Selecione a localização:</p>

            <div className={styles.localization}>
              <ButtonComponent
                className={styles.button}
                onClick={() => {
                  if (positionUser) {
                    getLocalization(positionUser.lat, positionUser.lng);
                    setErrorMap('');
                  }
                }}
              >
                <span className={styles.marker}>
                  <GiPositionMarker color="white" />
                </span>
                {loading ? (
                  <LoadingComponent mini color="neutral" />
                ) : (
                  <>
                    <p>Usar minha localização</p>
                  </>
                )}
              </ButtonComponent>
            </div>
            <p className={styles.or}>ou</p>
          </>
        )}

        <p className={styles.subTitle}>Selecione o seu estado e cidade.</p>
        <Form onSubmit={() => null} className={styles.form}>
          <SelectComponent
            options={states}
            name="state"
            label="Estado"
            placeholder="Selecione"
            onChange={e => {
              setState(e.target.value);
              setCity('');
            }}
            value={localization.state}
          />
          <div className={styles.select}>
            {viewSelect ? (
              <SelectComponent
                options={cities}
                name="city"
                label="Cidade"
                placeholder="Selecione"
                onChange={e => setCity(e.target.value)}
                value={city}
              />
            ) : (
              <>{state && <LoadingComponent mini />}</>
            )}
          </div>
          {state && city && (
            <ButtonComponent type="button" onClick={sendForm}>
              Enviar
            </ButtonComponent>
          )}
        </Form>
      </DrawerComponent>
    </>
  );
}
